import { lawvoFetchJson } from './httpClient';
import { mapFormToPermissions } from './permissionMapper';
import { createInitialFormData, FormData, normalizeDraftFormData } from '../types/form';
export type SavedRequestStatus = 'draft' | 'submitted';
export interface SavedGroupSetupRequest {
  id: number;
  formData: FormData;
  currentStep: number;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string | null;
  webhookSubmittedAt?: string | null;
  status: SavedRequestStatus;
  title?: string;
  permissions?: Record<string, unknown> | null;
  opsNotes?: Record<string, string> | null;
  groupId?: number | null;
}
interface FeathersList<T> {
  data: T[];
  total?: number;
}
interface ApiRecord {
  id: number;
  formData: Record<string, unknown>;
  currentStep: number;
  status: SavedRequestStatus;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string | null;
  webhookSubmittedAt?: string | null;
  title?: string;
  permissions?: Record<string, unknown> | null;
  opsNotes?: Record<string, string> | null;
  groupId?: number | null;
}
const LEGACY_DRAFT_KEY = 'lawvo-group-setup-draft';
const LEGACY_LIST_KEY = 'lawvo_group_setup_requests';
function mapRecord(record: ApiRecord): SavedGroupSetupRequest {
  return {
    id: record.id,
    formData: normalizeDraftFormData(record.formData || {}),
    currentStep: record.currentStep ?? 0,
    status: record.status === 'submitted' ? 'submitted' : 'draft',
    createdAt: record.createdAt,
    updatedAt: record.updatedAt,
    submittedAt: record.submittedAt,
    webhookSubmittedAt: record.webhookSubmittedAt,
    title: record.title,
    permissions: record.permissions,
    opsNotes: record.opsNotes,
    groupId: record.groupId
  };
}
export async function listSavedRequests(): Promise<SavedGroupSetupRequest[]> {
  const res = await lawvoFetchJson<FeathersList<ApiRecord>>('/group-setup-requests?$limit=200&deleted=false&$sort[updatedAt]=-1');
  return (res.data || []).map(mapRecord);
}
export async function getSavedRequest(id: number | string): Promise<SavedGroupSetupRequest | null> {
  try {
    const record = await lawvoFetchJson<ApiRecord>(`/group-setup-requests/${id}`);
    if (record && (record as ApiRecord & {
      deleted?: boolean;
    }).deleted) {
      return null;
    }
    return mapRecord(record);
  } catch {
    return null;
  }
}
export async function createSavedRequest(formData?: FormData, currentStep = 0): Promise<SavedGroupSetupRequest> {
  const data = formData ?? createInitialFormData();
  const mapping = mapFormToPermissions(data);
  const record = await lawvoFetchJson<ApiRecord>('/group-setup-requests', {
    method: 'POST',
    body: JSON.stringify({
      formData: data,
      currentStep,
      status: 'draft',
      permissions: mapping.permissions,
      opsNotes: mapping.opsNotes
    })
  });
  return mapRecord(record);
}
export async function upsertSavedRequest(input: {
  id: number;
  formData: FormData;
  currentStep: number;
  status: SavedRequestStatus;
  submittedAt?: string;
  webhookSubmittedAt?: string;
}): Promise<SavedGroupSetupRequest> {
  const mapping = mapFormToPermissions(input.formData);
  const record = await lawvoFetchJson<ApiRecord>(`/group-setup-requests/${input.id}`, {
    method: 'PATCH',
    body: JSON.stringify({
      formData: input.formData,
      currentStep: input.currentStep,
      status: input.status,
      permissions: mapping.permissions,
      opsNotes: mapping.opsNotes,
      submittedAt: input.submittedAt,
      webhookSubmittedAt: input.webhookSubmittedAt
    })
  });
  return mapRecord(record);
}
export async function deleteSavedRequest(id: number | string): Promise<void> {
  await lawvoFetchJson(`/group-setup-requests/${id}`, {
    method: 'DELETE'
  });
}
export async function markRequestSubmitted(id: number | string): Promise<SavedGroupSetupRequest | null> {
  const record = await lawvoFetchJson<ApiRecord>(`/group-setup-requests/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({
      status: 'submitted',
      submittedAt: new Date().toISOString()
    })
  });
  return mapRecord(record);
}
export function getRequestDisplayTitle(request: SavedGroupSetupRequest): string {
  if (request.title?.trim()) {
    const title = request.title.trim();
    return title.length > 80 ? `${title.slice(0, 77)}...` : title;
  }
  const description = request.formData.groupDescription?.trim();
  if (description) {
    return description.length > 80 ? `${description.slice(0, 77)}...` : description;
  }
  return 'Untitled Group Setup';
}

/** Import any browser-only drafts into lawvo-api once per browser. */
export async function migrateLegacyLocalStorage(): Promise<number> {
  let migrated = 0;
  try {
    const legacyDraft = localStorage.getItem(LEGACY_DRAFT_KEY);
    if (legacyDraft) {
      const parsed = JSON.parse(legacyDraft);
      if (parsed?.formData) {
        await createSavedRequest(normalizeDraftFormData(parsed.formData), parsed.currentStep ?? 0);
        migrated += 1;
      }
      localStorage.removeItem(LEGACY_DRAFT_KEY);
    }
  } catch {
    localStorage.removeItem(LEGACY_DRAFT_KEY);
  }
  try {
    const legacyList = localStorage.getItem(LEGACY_LIST_KEY);
    if (legacyList) {
      const parsed = JSON.parse(legacyList);
      if (Array.isArray(parsed)) {
        for (const item of parsed) {
          if (!item?.formData) continue;
          await createSavedRequest(normalizeDraftFormData(item.formData), item.currentStep ?? 0);
          migrated += 1;
        }
      }
      localStorage.removeItem(LEGACY_LIST_KEY);
    }
  } catch {
    localStorage.removeItem(LEGACY_LIST_KEY);
  }
  return migrated;
}